// Shared Tailwind class constants for the lifecare studio. Centralizing them keeps
// the component files focused on structure / Formik wiring instead of styling churn.

export const cardClasses =
  'bg-white border border-primary-surface border-l-4 border-l-primary-light-2 ' +
  'rounded-[calc(var(--spacing)*2.5)] py-4 px-5 mb-6';

export const cardHeaderClasses = 'flex items-center justify-between mb-3';

export const cardOrderClasses =
  'text-[calc(var(--spacing)*2.75)] font-bold leading-4 uppercase ' +
  'tracking-[calc(var(--spacing)*0.225)] text-subtle block mb-1';

export const cardTitleClasses = 'text-xl font-bold leading-7 text-content m-0';

export const fieldRowClasses = 'grid grid-cols-1 md:grid-cols-3 gap-4 mb-4';

export const fieldLabelClasses = 'block text-sm font-semibold text-subtle mb-1';

// deliberately not a scroll container: the table headers stick against the studio's own
// scroll area, and any overflow between them and it would trap the sticky positioning
export const tableWrapperClasses = 'mt-2';

// table-fixed splits the width evenly across eight columns, so in a narrow container the
// cells get thin enough that a long value wraps to a few characters per line and the
// auto-growing textarea turns into a tower. Below this floor the wrapper scrolls sideways.
export const tableClasses =
  'min-w-[max(100%,64rem)] border border-border-light border-collapse table-fixed';

// The studio tab bar is itself sticky at the top of the scroll area, so the table header
// parks right under it instead of at top-0, where it would be hidden behind the tabs. The
// tab bar's height is measured at runtime and published as this variable by the studio
// body. z-1 lifts the header above the rows scrolling beneath it.
export const stickyHeaderOffsetVar = '--lifecare-sticky-header-offset';

export const tableHeadCellClasses =
  'sticky top-[var(--lifecare-sticky-header-offset,0px)] z-1 ' +
  'bg-primary-surface text-subtle text-xs font-bold uppercase ' +
  'border-b border-t border-border-light border-r border-r-border-light last:border-r-0 ' +
  'align-middle py-2.5 px-2 text-left';

export const tableBodyCellClasses =
  'align-top py-2 px-2 bg-white border-r border-r-border-light border-b border-b-border-light last:border-r-0 ' +
  'focus-within:shadow-[inset_0_0_0_2px_var(--color-primary-tint)] focus-within:bg-surface-light';

export const tableInputClasses =
  'w-full h-9 px-2 py-1 font-sans text-sm text-content ' +
  'bg-transparent border border-transparent rounded ' +
  'focus:outline-none focus:border-primary focus:bg-white';

export const tableTextareaClasses =
  'block w-full min-h-[2.25rem] px-2 py-1 font-sans text-sm leading-5 text-content ' +
  'bg-transparent border border-transparent rounded resize-none overflow-hidden ' +
  'focus:outline-none focus:border-primary focus:bg-white';

// the Age / Year cell stacks a Beginning and an Ending sub row, each splitting the
// cell into an age and a year input, plus the life-expectancy toggle for the ending
export const ageYearCellClasses = 'flex flex-col gap-1';

export const ageYearSubRowClasses = 'flex flex-col';

export const ageYearSubRowLabelClasses =
  'text-[0.625rem] font-bold uppercase tracking-wide text-subtle px-2';

export const ageYearInputsRowClasses = 'grid grid-cols-2 gap-1';

export const ageYearInputClasses =
  'w-full h-8 px-1.5 py-1 font-sans text-sm text-content ' +
  'bg-transparent border border-transparent rounded ' +
  'focus:outline-none focus:border-primary focus:bg-white';

export const ageYearToggleClasses =
  'flex items-center gap-1.5 px-2 text-[0.625rem] font-semibold uppercase tracking-wide text-subtle cursor-pointer';

// seed heights handed to the auto-grow hook so a field never starts shorter than the
// fixed-height input it replaced (the hook then tracks scrollHeight from there)
export const tableCellMinHeight = '2.25rem';

export const subsectionTitleMinHeight = '2.375rem';

export const fieldMinHeight = '2.5rem';

export const totalsCellClasses =
  'text-right text-sm font-semibold text-content bg-surface-light';

export const inlineButtonClasses =
  'inline-flex items-center gap-1.5 text-xs font-semibold ' +
  'py-1 px-2.5 rounded border border-border text-subtle bg-white ' +
  'hover:border-primary hover:text-primary hover:bg-primary/[0.05] ' +
  'disabled:opacity-50 disabled:cursor-not-allowed';

export const dangerInlineButtonClasses =
  'inline-flex items-center gap-1.5 text-xs font-semibold ' +
  'py-1 px-2.5 rounded border border-transparent text-danger bg-transparent ' +
  'hover:bg-danger/10';

// the row actions stack vertically so the move button sits directly below the trash icon
export const rowActionsCellClasses = 'flex flex-col items-center gap-1';

export const neutralInlineButtonClasses =
  'inline-flex items-center gap-1.5 text-xs font-semibold ' +
  'py-1 px-2.5 rounded border border-transparent text-subtle bg-transparent ' +
  'hover:text-primary hover:bg-primary/[0.08] ' +
  'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-subtle';

export const rowMoveMenuWrapperClasses = 'relative inline-flex';

export const rowMoveMenuClasses =
  'absolute right-0 top-full mt-1 z-10 min-w-48 max-w-64 max-h-60 overflow-y-auto ' +
  'rounded-md border border-border bg-white py-1 text-left ' +
  'shadow-[0_4px_16px_rgba(0,0,0,0.18),0_1px_4px_rgba(0,0,0,0.12)]';

export const rowMoveMenuItemClasses =
  'block w-full truncate px-3 py-2 text-left text-xs font-medium text-content ' +
  'hover:bg-primary/[0.08] hover:text-primary';

export const subsectionWrapperClasses =
  'border border-border rounded-xl p-4 mb-4 bg-surface-card/40';

export const subsectionHeaderClasses =
  'flex items-center justify-between gap-3 mb-3';

export const subsectionTitleInputClasses =
  'block w-full font-secondary text-lg leading-7 font-semibold text-content ' +
  'bg-transparent border border-transparent rounded px-2 py-1 ' +
  'resize-none overflow-hidden ' +
  'focus:outline-none focus:border-primary focus:bg-white';

export const summaryTableClasses =
  'w-full border border-border-light border-collapse table-fixed';

export const summaryRowClasses = 'odd:bg-white even:bg-surface-light';

export const summaryTotalRowClasses = 'bg-primary-surface font-bold';

export const autosaveBadgeClasses =
  'inline-flex items-center gap-1.5 text-xs font-semibold ' +
  'py-1 px-2.5 rounded-full';

export const autosaveStatusToClasses = {
  idle: 'bg-surface-muted text-subtle',
  saving: 'bg-primary-surface text-primary',
  saved: 'bg-success-surface text-success',
  failed: 'bg-danger-surface text-danger',
};
